import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function CategoryDropdown({ id, label, open, onToggle, onClose, children }) {
  const trigger = useRef(null);
  const panel = useRef(null);
  const focusFirst = useRef(false);
  const [position, setPosition] = useState({});

  useLayoutEffect(() => {
    if (!open) return;
    const inside = target => trigger.current?.contains(target) || panel.current?.contains(target);
    const place = () => {
      const rect = trigger.current.getBoundingClientRect();
      const viewport = window.visualViewport;
      const leftEdge = viewport?.offsetLeft || 0;
      const topEdge = viewport?.offsetTop || 0;
      const width = viewport?.width || window.innerWidth;
      const height = viewport?.height || window.innerHeight;
      const menuWidth = Math.min(240, width - 16);
      const below = topEdge + height - rect.bottom - 16;
      const above = rect.top - topEdge - 16;
      const upwards = below < 180 && above > below;
      setPosition({
        width: menuWidth,
        left: Math.max(leftEdge + 8, Math.min(rect.left, leftEdge + width - menuWidth - 8)),
        top: upwards ? undefined : rect.bottom + 4,
        bottom: upwards ? window.innerHeight - rect.top + 4 : undefined,
        maxHeight: Math.max(44, upwards ? above : below),
      });
    };
    const dismiss = event => { if (!inside(event.target)) onClose(); };
    const keyboard = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        trigger.current?.focus();
      }
    };
    const scroll = event => {
      if (!panel.current?.contains(event.target)) place();
    };
    place();
    if (focusFirst.current) {
      panel.current?.querySelector("a, button:not(:disabled)")?.focus();
      focusFirst.current = false;
    }
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("focusin", dismiss);
    document.addEventListener("keydown", keyboard);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", scroll, true);
    window.visualViewport?.addEventListener("resize", place);
    window.visualViewport?.addEventListener("scroll", place);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("focusin", dismiss);
      document.removeEventListener("keydown", keyboard);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", scroll, true);
      window.visualViewport?.removeEventListener("resize", place);
      window.visualViewport?.removeEventListener("scroll", place);
    };
  }, [open, onClose]);

  return <>
    <button ref={trigger} id={`${id}-trigger`} type="button"
      className="content-bar-link has-dropdown" aria-expanded={open}
      aria-controls={open ? `${id}-panel` : undefined}
      onClick={onToggle}
      onKeyDown={event => {
        if (event.key === "ArrowDown" || (event.key === "Tab" && !event.shiftKey && open)) {
          event.preventDefault();
          if (open) panel.current?.querySelector("a, button:not(:disabled)")?.focus();
          else {
            focusFirst.current = true;
            onToggle();
          }
        }
      }}>
      {label}<span className="dropdown-arrow" aria-hidden="true">▼</span>
    </button>
    {open && createPortal(
      <div ref={panel} id={`${id}-panel`} aria-labelledby={`${id}-trigger`}
        className="subcategory-dropdown show" style={position}
        onKeyDown={event => {
          const first = panel.current.querySelector("a, button:not(:disabled)");
          if (event.key === "Tab" && event.shiftKey && event.target === first) {
            event.preventDefault();
            trigger.current.focus();
            onClose();
          }
          if (event.key === "Tab" && !event.shiftKey) {
            const links = panel.current.querySelectorAll("a, button:not(:disabled)");
            if (event.target === links[links.length - 1]) {
              onClose();
              trigger.current.focus();
            }
          }
        }}>
        {children}
      </div>, document.body)}
  </>;
}